// Session JSONL export (#812) — fetch the full trail from
// `/v1/sessions/:id/events.jsonl` and hand it to the browser as a download.
// Mirrors `sessionStream`'s auth: the session cookie rides
// `credentials: 'include'`, a real dev PAT becomes a bearer, and the
// URL-derived org context (#460) tags along.
//
// A drain the route cut short (its deadline, or a store read that failed after
// rows were written) still answers 200, and its LAST line says so:
// `{"truncated":true,"next_after":"<cursor>"}` (`SessionExportTrailer`,
// `apps/api/src/routes/session-events.ts`). This module follows `next_after` as
// `?after=` until a body ends without one, and saves the rows only — the export
// once wrote whatever arrived, saving a partial audit trail as a success
// (#5608 slice 17 row 19). Any failed request fails the whole export: a file
// missing a slice of the trail is worse than no file.

import { z } from 'zod';
import { activeAccountId } from './active-account';
import { API_BASE_URL } from './api';
import { ApiError } from './api-errors';
import { downloadBlob } from './download-blob';
import { SESSION_TOKEN } from './session';
import { markSessionExpired } from './session-expiry';

const TrailerSchema = z.object({ truncated: z.literal(true), next_after: z.string().min(1) });

/**
 * The trailer's exact opening, as the route serializes it
 * (`JSON.stringify({ truncated: true, next_after })`). A row is an audit event
 * and never starts with it, so the prefix decides; the one parse after it must
 * then succeed — a malformed trailer is a broken export, thrown, never a row.
 */
const TRAILER_PREFIX = '{"truncated":true,';

/** `body` split into its rows and the trailer's cursor, when it ends with one. */
function splitTrailer(body: string): { rows: string; nextAfter: string | null } {
  const trimmed = body.endsWith('\n') ? body.slice(0, -1) : body;
  const cut = trimmed.lastIndexOf('\n');
  const last = trimmed.slice(cut + 1);
  if (!last.startsWith(TRAILER_PREFIX)) return { rows: body, nextAfter: null };
  const trailer = TrailerSchema.parse(JSON.parse(last));
  return {
    rows: cut === -1 ? '' : `${trimmed.slice(0, cut)}\n`,
    nextAfter: trailer.next_after,
  };
}

/** The whole trail as one `.jsonl` blob, every continuation followed. */
export async function fetchSessionJsonl(
  sessionId: string,
  token: string | null = SESSION_TOKEN,
): Promise<Blob> {
  const org = activeAccountId();
  const path = `/v1/sessions/${encodeURIComponent(sessionId)}/events.jsonl`;
  const parts: string[] = [];
  let after: string | null = null;
  for (;;) {
    const url = after === null ? path : `${path}?after=${encodeURIComponent(after)}`;
    const res = await fetch(`${API_BASE_URL}${url}`, {
      credentials: 'include',
      headers: {
        ...(token?.startsWith('dev_pat_') ? { authorization: `Bearer ${token}` } : {}),
        ...(org !== null ? { 'x-dz-account': org } : {}),
      },
    });
    if (!res.ok) {
      // A raw `fetch`, so the 401 latch is set here as `apiFetch` would.
      if (res.status === 401) markSessionExpired();
      throw new ApiError(res.status, `GET ${url} → ${res.status}`);
    }
    const { rows, nextAfter } = splitTrailer(await res.text());
    parts.push(rows);
    if (nextAfter === null) return new Blob(parts, { type: 'application/x-ndjson' });
    // A trailer naming the cursor it was asked from made no progress: following
    // it would loop for ever over the same request.
    if (nextAfter === after) {
      throw new ApiError(res.status, `GET ${url} → truncated at its own cursor ${after}`);
    }
    after = nextAfter;
  }
}

export async function downloadSessionJsonl(
  sessionId: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  downloadBlob(`${sessionId}.jsonl`, await fetchSessionJsonl(sessionId, token));
}
